<script setup lang="ts">
  import { computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import moment from "moment";
  import { Button } from "@/components/ui/button";
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import { Banner } from "@/components/episto-ui/banner";
  import { usePricerProjectStore } from "@/stores/pricer/pricerProject";
  import { usePricerQuoteStore } from "@/stores/pricer/pricerQuote";
  import { formatCurrency, formatInteger } from "@/utils/pricer/pricerHelpers";
  import type { PricerProject } from "@/types";

  const props = defineProps<{ open: boolean }>();
  const emit = defineEmits<{ "update:open": [value: boolean] }>();

  const { project: storedProject, hasProjectChanged } = storeToRefs(usePricerProjectStore());
  const project = computed<Partial<PricerProject>>(() => storedProject.value);
  const quoteStore = usePricerQuoteStore();
  const { preview, error, isLoading, isGenerating } = storeToRefs(quoteStore);

  watch(
    () => props.open,
    (isOpen) => {
      if (isOpen && project.value.token) quoteStore.fetchPreview(project.value.token);
    },
    { immediate: true },
  );

  const isUpdate = computed(() => preview.value?.mode === "update");
  const replacesPublishedQuote = computed(
    () => preview.value?.mode === "create" && Boolean(preview.value?.hs_quote_id),
  );
  const hasDeal = computed(() => Boolean(project.value.crm_id));
  const SETTING_LABELS = { title: "title", terms: "purchase terms", language: "language" };
  const changedSettings = computed(() => (preview.value?.changes ?? []).map((change) => SETTING_LABELS[change]));
  const footerMessage = computed(() => {
    if (!isUpdate.value) return "";
    if (changedSettings.value.length) {
      return `Pricer lines and the ${changedSettings.value.join(
        ", ",
      )} of the draft are refreshed; comments to buyer and lines added by hand are kept`;
    }
    return "Only the pricer lines are refreshed; everything else in the draft stays as it is";
  });
  const canConfirm = computed(
    () => hasDeal.value && !hasProjectChanged.value && !isGenerating.value && Boolean(preview.value?.lines.length),
  );

  const parameters = computed(() => {
    if (!preview.value) return [];
    return [
      { label: "Title", value: preview.value.title },
      { label: "Language", value: preview.value.language === "fr" ? "French" : "English" },
      { label: "Currency", value: preview.value.currency },
      { label: "Expiration", value: moment(preview.value.expiration_date).format("DD/MM/YYYY") },
    ];
  });

  const handleConfirm = async () => {
    if (!project.value.token) return;
    const started = await quoteStore.generateQuote(project.value.token);
    if (started) emit("update:open", false);
  };
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)">
    <DialogContent
      class="sm:max-w-[60%] max-h-[90vh] flex flex-col"
      :show-close-button="false">
      <DialogHeader>
        <DialogTitle class="heading-title">
          {{ isUpdate ? "Update quote in HubSpot" : "Create quote in HubSpot" }}
        </DialogTitle>
        <p class="text-sm text-muted-foreground mt-1">
          {{
            isUpdate
              ? "Review the lines before updating the draft quote in HubSpot"
              : "A draft quote will be created on the deal, to review and publish in HubSpot"
          }}
        </p>
      </DialogHeader>

      <LoadingState
        v-if="isLoading"
        label="Preparing the quote..."
        class="flex-1 min-h-[300px]" />

      <Banner v-if="error && !isLoading">
        <span class="font-semibold">HubSpot error:</span>
        {{ error }}
      </Banner>

      <template v-if="preview && !isLoading">
        <Banner v-if="!hasDeal">Create the deal in HubSpot first.</Banner>
        <Banner v-else-if="hasProjectChanged">
          <span class="font-semibold">Warning:</span>
          This project has unsaved modifications. You must save the project before generating the quote.
        </Banner>
        <Banner
          v-if="replacesPublishedQuote"
          variant="info">
          The previous quote will not be updated: it stays as it is and a new draft will be created.
        </Banner>

        <div class="border rounded-md overflow-hidden">
          <div class="overflow-y-auto max-h-[400px]">
            <Table>
              <TableHeader class="sticky top-0 bg-background z-10">
                <TableRow class="hover:bg-transparent border-b border-border">
                  <TableHead class="text-sm font-medium text-muted-foreground">Item</TableHead>
                  <TableHead class="text-sm font-medium text-muted-foreground text-right">Quantity</TableHead>
                  <TableHead class="text-sm font-medium text-muted-foreground text-right">Unit price</TableHead>
                  <TableHead class="text-sm font-medium text-muted-foreground text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow
                  v-for="line in preview.lines"
                  :key="line.key"
                  class="hover:bg-muted/50 border-b border-border">
                  <TableCell class="font-medium">{{ line.label }}</TableCell>
                  <TableCell class="text-right">{{ formatInteger(line.quantity) }}</TableCell>
                  <TableCell class="text-right">{{ formatCurrency(line.unit_price) }}</TableCell>
                  <TableCell class="text-right font-medium">{{ formatCurrency(line.amount) }}</TableCell>
                </TableRow>
                <TableRow v-if="!preview.lines.length">
                  <TableCell
                    colspan="4"
                    class="text-center text-muted-foreground">
                    The Summary has no priced line to quote
                  </TableCell>
                </TableRow>
              </TableBody>
              <TableFooter class="bg-transparent">
                <TableRow class="hover:bg-transparent">
                  <TableCell
                    colspan="3"
                    class="text-right">
                    Subtotal
                  </TableCell>
                  <TableCell class="text-right font-semibold">{{ formatCurrency(preview.subtotal) }}</TableCell>
                </TableRow>
                <TableRow class="hover:bg-transparent">
                  <TableCell
                    colspan="3"
                    class="text-right text-muted-foreground">
                    VAT {{ preview.vat_rate }} %
                  </TableCell>
                  <TableCell class="text-right">{{ formatCurrency(preview.vat) }}</TableCell>
                </TableRow>
                <TableRow class="hover:bg-transparent">
                  <TableCell
                    colspan="3"
                    class="text-right font-semibold text-primary">
                    Total
                  </TableCell>
                  <TableCell class="text-right font-semibold">{{ formatCurrency(preview.total) }}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </div>

        <dl class="grid grid-cols-4 gap-4">
          <div
            v-for="parameter in parameters"
            :key="parameter.label">
            <dt class="heading-label text-muted-foreground">{{ parameter.label }}</dt>
            <dd class="text-sm font-medium truncate">{{ parameter.value }}</dd>
          </div>
        </dl>
      </template>

      <div
        v-if="!isLoading"
        class="flex justify-between items-center pt-4 border-t border-border">
        <p class="text-sm text-muted-foreground">{{ footerMessage }}</p>
        <div class="flex gap-2">
          <Button
            variant="outline"
            @click="emit('update:open', false)">
            Cancel
          </Button>
          <Button
            :disabled="!canConfirm"
            @click="handleConfirm">
            <span v-if="isGenerating">Generating...</span>
            <span v-else>{{ isUpdate ? "Update quote" : "Create quote" }}</span>
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
